import { 
  BrowserSupport, 
  PushSubscriptionData, 
  NotificationPayload,
  PushNotificationError,
  PushNotificationErrorDetails
} from '../types/notifications';

/**
 * Check if the browser supports push notifications
 */
export function checkBrowserSupport(): BrowserSupport {
  return {
    serviceWorker: 'serviceWorker' in navigator,
    pushManager: 'PushManager' in window,
    notifications: 'Notification' in window,
    promise: 'Promise' in window
  };
}

/**
 * Check if all required features are supported
 */
export function isPushNotificationSupported(): boolean {
  const support = checkBrowserSupport();
  return support.serviceWorker && support.pushManager && support.notifications && support.promise;
}

/**
 * Convert a VAPID public key from base64 to Uint8Array
 */
export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Convert PushSubscription to a serializable object
 */
export function subscriptionToJSON(subscription: PushSubscription): PushSubscriptionData {
  const keys = subscription.getKey ? {
    p256dh: arrayBufferToBase64(subscription.getKey('p256dh')!),
    auth: arrayBufferToBase64(subscription.getKey('auth')!)
  } : { p256dh: '', auth: '' };

  return {
    endpoint: subscription.endpoint,
    keys
  };
}

/**
 * Convert ArrayBuffer to base64 string
 */
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const binaryString = Array.from(bytes, byte => String.fromCharCode(byte)).join('');
  return window.btoa(binaryString);
}

/**
 * Request notification permission from the user
 */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) {
    throw new Error('Notifications are not supported');
  }

  let permission = Notification.permission;

  if (permission === 'default') {
    permission = await Notification.requestPermission();
  }

  return permission;
}

/**
 * Register service worker
 */
export async function registerServiceWorker(swPath: string = '/sw.js'): Promise<ServiceWorkerRegistration> {
  if (!('serviceWorker' in navigator)) {
    throw new Error('Service workers are not supported');
  }

  try {
    const registration = await navigator.serviceWorker.register(swPath);
    console.log('Service Worker registered successfully:', registration);
    
    // Wait for the service worker to be ready
    await navigator.serviceWorker.ready;
    console.log('Service Worker is active and ready');
    
    return registration;
  } catch (error) {
    console.error('Service Worker registration failed:', error);
    throw error;
  }
}

/**
 * Subscribe to push notifications
 */
export async function subscribeToPushNotifications(
  registration: ServiceWorkerRegistration,
  vapidPublicKey: string
): Promise<PushSubscription> {
  if (!registration.pushManager) {
    throw new Error('Push messaging is not supported');
  }

  try {
    const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);
    
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: applicationServerKey as BufferSource
    });

    console.log('Push subscription successful:', subscription);
    return subscription;
  } catch (error) {
    console.error('Push subscription failed:', error);
    throw error;
  }
}

/**
 * Unsubscribe from push notifications
 */
export async function unsubscribeFromPushNotifications(
  registration: ServiceWorkerRegistration
): Promise<boolean> {
  if (!registration.pushManager) {
    return false;
  }

  try {
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      const successful = await subscription.unsubscribe();
      console.log('Unsubscribed from push notifications:', successful);
      return successful;
    }
    return true;
  } catch (error) {
    console.error('Unsubscribe failed:', error);
    throw error;
  }
}

/**
 * Get existing push subscription
 */
export async function getExistingSubscription(
  registration: ServiceWorkerRegistration
): Promise<PushSubscription | null> {
  if (!registration.pushManager) {
    return null;
  }

  try {
    return await registration.pushManager.getSubscription();
  } catch (error) {
    console.error('Failed to get existing subscription:', error);
    return null;
  }
}

/**
 * Send subscription to server
 */
export async function sendSubscriptionToServer(
  subscription: PushSubscriptionData,
  serverEndpoint: string
): Promise<boolean> {
  try {
    const response = await fetch(serverEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(subscription),
    });

    if (!response.ok) {
      throw new Error(`Server responded with status: ${response.status}`);
    }

    console.log('Subscription sent to server successfully');
    return true;
  } catch (error) {
    console.error('Failed to send subscription to server:', error);
    throw error;
  }
}

/**
 * Show a local notification (for testing)
 */
export function showLocalNotification(payload: NotificationPayload): void {
  if (!('Notification' in window)) {
    console.error('Notifications are not supported');
    return;
  }

  if (Notification.permission === 'granted') {
    const notification = new Notification(payload.title, {
      body: payload.body,
      icon: payload.icon,
      badge: payload.badge,
      tag: payload.tag,
      data: payload.data,
      requireInteraction: payload.requireInteraction,
      silent: payload.silent
    });

    notification.onclick = () => {
      if (payload.data?.url) {
        window.open(payload.data.url, '_blank');
      }
      notification.close();
    };

    // Auto-close after 5 seconds if not set to require interaction
    if (!payload.requireInteraction) {
      setTimeout(() => {
        notification.close();
      }, 5000);
    }
  }
}

/**
 * Create a detailed error object for push notification errors
 */
export function createPushNotificationError(
  type: PushNotificationError,
  message: string,
  originalError?: Error
): PushNotificationErrorDetails {
  return {
    type,
    message,
    originalError
  };
}

/**
 * Generate a VAPID key pair (for development/testing)
 * Note: In production, you should generate these server-side
 */
export async function generateVapidKeys(): Promise<{ publicKey: string; privateKey: string }> {
  // This is a simplified version for demonstration
  // In a real app, you'd use a proper VAPID key generation library
  console.warn('generateVapidKeys should only be used for development. Use proper server-side key generation for production.');
  
  // For now, return placeholder keys
  // You'll need to replace these with actual VAPID keys from your server
  return {
    publicKey: 'YOUR_VAPID_PUBLIC_KEY_HERE',
    privateKey: 'YOUR_VAPID_PRIVATE_KEY_HERE'
  };
}

/**
 * Check if user has already granted notification permission
 */
export function hasNotificationPermission(): boolean {
  return 'Notification' in window && Notification.permission === 'granted';
}

/**
 * Check if the app is currently subscribed to push notifications
 */
export async function isSubscribedToPush(
  registration: ServiceWorkerRegistration
): Promise<boolean> {
  const subscription = await getExistingSubscription(registration);
  return subscription !== null;
}

/**
 * Utility to validate VAPID public key format
 */
export function isValidVapidKey(key: string): boolean {
  try {
    // VAPID keys should be base64 URL-safe encoded and 65 bytes when decoded
    const decoded = urlBase64ToUint8Array(key);
    return decoded.length === 65;
  } catch {
    return false;
  }
}